import React, { useState } from "react";

import { Calendar, Eye } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Progress } from "~/components/ui/progress";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import PaymentDetailsDrawer from "~/pages/payments/components/PaymentDetailsDrawer";
import { formatDate } from "~/utils/helpers";

interface OutstandingPaymentsTableProps {
  outstandingPayments: any[];
  totalOutstandingPayments: number;
  isLoading: boolean;
}

const OutstandingPaymentsTable: React.FC<OutstandingPaymentsTableProps> = ({
  outstandingPayments,
  totalOutstandingPayments,
  isLoading,
}) => {
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );

  if (isLoading) {
    return (
      <div className="mb-8 w-full rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Pagos Pendientes</h3>
          <TableSkeleton rows={5} columns={5} />
        </div>
      </div>
    );
  }

  if (!outstandingPayments || outstandingPayments.length === 0) {
    return (
      <div className="mb-8 w-full rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Pagos Pendientes</h3>
          <div className="py-8 text-center">
            <p className="text-muted-foreground">
              No hay pagos pendientes por vencer
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 w-full rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h3 className="mb-4 text-lg font-semibold">
          Pagos Pendientes (Por vencer)
        </h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Fecha Emisión</TableHead>
                <TableHead>Días Restantes</TableHead>
                <TableHead className="w-1/3">Estado de Pago</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {outstandingPayments.map((payment, index) => {
                const totalAmount = Number(payment.total_amount);
                const paidAmount = Number(payment.paid_amount || 0);
                const remainingAmount =
                  payment.remaining_amount || totalAmount - paidAmount;
                const progressPercentage =
                  totalAmount > 0
                    ? Math.min(100, (paidAmount / totalAmount) * 100)
                    : 0;

                return (
                  <TableRow
                    key={payment.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      {payment.client?.name || "Cliente desconocido"}
                    </TableCell>
                    <TableCell>{formatDate(payment.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-amber-500" />
                        <span className="font-medium text-amber-600">
                          {payment.daysRemaining} días
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>
                            {paidAmount > 0
                              ? `$${paidAmount.toLocaleString()}`
                              : "Sin pagos"}
                          </span>
                          <span>${totalAmount.toLocaleString()}</span>
                        </div>
                        <Progress value={progressPercentage} className="h-2" />
                        <div className="mt-1 flex justify-between text-xs">
                          <span className="text-muted-foreground">
                            {payment.payments && payment.payments.length > 0
                              ? `${payment.payments.length} pago(s) registrado(s)`
                              : "Sin pagos registrados"}
                          </span>
                          <span className="font-medium text-amber-600">
                            Pendiente: ${remainingAmount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedPaymentId(payment.id)}
                      >
                        <Eye className="mr-1 h-4 w-4" /> Ver detalles
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <span className="text-sm text-muted-foreground">
            {outstandingPayments.length} pagos pendientes
          </span>
          <div className="text-right">
            <span className="font-medium">
              Total pendiente: ${totalOutstandingPayments.toLocaleString()}
            </span>
            <p className="text-xs text-muted-foreground">
              Calculado después de pagos parciales
            </p>
          </div>
        </div>
      </div>

      {/* Payment Details Drawer */}
      <PaymentDetailsDrawer
        paymentId={selectedPaymentId}
        open={!!selectedPaymentId}
        onClose={() => setSelectedPaymentId(null)}
        payment={outstandingPayments.find((p) => p.id === selectedPaymentId)}
      />
    </div>
  );
};

export default OutstandingPaymentsTable;
